import React from "react";

export default function PerceptualResponseInput({
  constructLabel,
  instruction,
  responseFamilyLabel,
  sourceParameterLabel,
  options = [],
  scaleFamily,
  min = 0,
  max = 100,
  integer = false,
  value,
  onChange,
  endpointLabels,
  visuallyBlindValue = false,
  hideHandleUntilInteraction = false,
  visualInitialValue = min,
  disabled = false,
  showIdentity = true,
  leadingContent,
  sectionLabel,
  inputRef,
  invalid = false,
  validationMessage,
}) {
  const inputName = `perceptual-${String(constructLabel).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const validationId = `${inputName}-validation`;

  if (scaleFamily === "categorical" && min === 0 && max === 3 && integer) {
    return (
      <fieldset className="av-response-control av-response-control--categorical" disabled={disabled} data-invalid={invalid || undefined} aria-invalid={invalid || undefined} aria-describedby={invalid && validationMessage ? validationId : undefined}>
        <legend className="av-group-legend--sr-only">{constructLabel}</legend>
        {showIdentity && <h3 className="av-group-heading">{constructLabel}</h3>}
        {showIdentity && (responseFamilyLabel || sourceParameterLabel) && (
          <p className="av-response-control__context">
            {sourceParameterLabel && <strong>{sourceParameterLabel}</strong>}
            {sourceParameterLabel && responseFamilyLabel && " — "}
            {responseFamilyLabel}
          </p>
        )}
        {leadingContent}
        {sectionLabel && <p className="av-response-control__section-label">{sectionLabel}</p>}
        {instruction && <p className="av-response-control__help">{instruction}</p>}
        <div className="av-categorical-options">
          {options.map((option, optionIndex) => (
            <label key={option.value} className="av-categorical-option">
              <input
                ref={optionIndex === 0 ? inputRef : undefined}
                type="radio"
                name={inputName}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange?.(option.value)}
              />
              <span className="av-categorical-option__value">{option.value}</span>
              <span className="av-categorical-option__label">{option.label}</span>
            </label>
          ))}
        </div>
        {invalid && validationMessage && <p id={validationId} className="av-response-control__validation">{validationMessage}</p>}
      </fieldset>
    );
  }

  const displayedValue = value ?? Number(visualInitialValue);
  const leftEndpoint = endpointLabels?.min ?? `${min} — Least`;
  const rightEndpoint = endpointLabels?.max ?? `${max} — Most`;
  return (
    <fieldset className="av-response-control av-response-control--continuous" disabled={disabled} data-invalid={invalid || undefined}>
      <legend className="av-group-legend--sr-only">{constructLabel}</legend>
      {showIdentity && <h3 className="av-group-heading">{constructLabel}</h3>}
      {showIdentity && (responseFamilyLabel || sourceParameterLabel) && (
        <p className="av-response-control__context">
          {sourceParameterLabel && <strong>{sourceParameterLabel}</strong>}
          {sourceParameterLabel && responseFamilyLabel && " — "}
          {responseFamilyLabel}
        </p>
      )}
      {leadingContent}
      {sectionLabel && <p className="av-response-control__section-label">{sectionLabel}</p>}
      <div className="av-response-control__heading">
        <p className="av-response-control__help">{instruction}</p>
        {!visuallyBlindValue && <output aria-live="polite">{value ?? "Not set"}</output>}
      </div>
      <input
        ref={inputRef}
        aria-label={`${constructLabel} continuous rating`}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid && validationMessage ? validationId : undefined}
        className={`av-response-slider${hideHandleUntilInteraction && (value === undefined || value === "") ? " av-range--unanswered" : ""}`}
        type="range"
        min={min}
        max={max}
        step={integer ? 1 : 1}
        value={displayedValue}
        onChange={event => onChange?.(Number(event.target.value))}
        onPointerDown={() => {
          if (value === undefined || value === "") onChange?.(Number(visualInitialValue));
        }}
      />
      <div className="av-response-endpoints" aria-hidden="true">
        <span>{leftEndpoint}</span>
        <span>{rightEndpoint}</span>
      </div>
      {invalid && validationMessage && <p id={validationId} className="av-response-control__validation">{validationMessage}</p>}
    </fieldset>
  );
}
